/**
 * Coupon More Module
 * Row-based view more and per-row horizontal controls
 */
export class CouponMore {
  constructor() {
    this.root = document.querySelector('.l-coupon');
    if (!this.root) {
      return;
    }

    this.button = this.root.querySelector('.l-coupon__more-btn');
    this.rows = Array.from(this.root.querySelectorAll('.l-coupon__row'))
      .map((row) => {
        const line = row.querySelector('.l-coupon__list');

        if (!line) {
          return null;
        }

        return {
          row,
          line,
          items: Array.from(row.querySelectorAll('.l-coupon__item')),
          nav: row.querySelector('.l-coupon__nav'),
          prevButton: row.querySelector('.l-coupon__arrow--prev'),
          nextButton: row.querySelector('.l-coupon__arrow--next'),
          indicatorThumb: row.querySelector('.l-coupon__indicator-thumb'),
          onScroll: null,
          onPointerDown: null,
          onPointerMove: null,
          onPointerUp: null,
          onClick: null,
          onDragStart: null,
          onPrevClick: null,
          onNextClick: null,
          dragStartX: 0,
          dragStartScrollLeft: 0,
          dragPointerId: null,
          isPointerDown: false,
          isDragging: false,
          suppressClick: false,
        };
      })
      .filter(Boolean);

    if (!this.button || this.rows.length === 0) {
      return;
    }

    this.initialActiveRows = 1;
    this.rowStep = 1;
    this.activeRows = this.initialActiveRows;
    this.resizeRaf = null;
    this.onResize = this.handleResize.bind(this);
    this.onWindowLoad = this.handleWindowLoad.bind(this);

    this.init();
  }

  init() {
    this.rows.forEach((rowState) => {
      this.bindRowEvents(rowState);
    });
    this.syncRowsToStart();
    this.render();
    requestAnimationFrame(() => {
      this.syncRowsToStart();
    });
    window.addEventListener('resize', this.onResize);
    window.addEventListener('load', this.onWindowLoad);

    this.onMoreClick = () => {
      this.showMore();
    };
    this.button.addEventListener('click', this.onMoreClick);
  }

  handleResize() {
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
    }

    this.resizeRaf = requestAnimationFrame(() => {
      this.resizeRaf = null;
      this.render();
    });
  }

  handleWindowLoad() {
    this.syncRowsToStart();
  }

  destroy() {
    if (!this.root) {
      return;
    }

    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('load', this.onWindowLoad);

    if (this.onMoreClick) {
      this.button.removeEventListener('click', this.onMoreClick);
      this.onMoreClick = null;
    }

    this.rows.forEach((rowState) => {
      if (rowState.onScroll) {
        rowState.line.removeEventListener('scroll', rowState.onScroll);
        rowState.onScroll = null;
      }

      if (rowState.onPointerDown) {
        rowState.line.removeEventListener('pointerdown', rowState.onPointerDown);
        rowState.onPointerDown = null;
      }

      if (rowState.onPointerMove) {
        rowState.line.removeEventListener('pointermove', rowState.onPointerMove);
        rowState.onPointerMove = null;
      }

      if (rowState.onPointerUp) {
        rowState.line.removeEventListener('pointerup', rowState.onPointerUp);
        rowState.line.removeEventListener('pointercancel', rowState.onPointerUp);
        rowState.line.removeEventListener('lostpointercapture', rowState.onPointerUp);
        rowState.onPointerUp = null;
      }

      if (rowState.onClick) {
        rowState.line.removeEventListener('click', rowState.onClick, true);
        rowState.onClick = null;
      }

      if (rowState.onDragStart) {
        rowState.line.removeEventListener('dragstart', rowState.onDragStart);
        rowState.onDragStart = null;
      }

      if (rowState.prevButton && rowState.onPrevClick) {
        rowState.prevButton.removeEventListener('click', rowState.onPrevClick);
        rowState.onPrevClick = null;
      }

      if (rowState.nextButton && rowState.onNextClick) {
        rowState.nextButton.removeEventListener('click', rowState.onNextClick);
        rowState.onNextClick = null;
      }
    });

    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = null;
    }
  }

  bindRowEvents(rowState) {
    rowState.onScroll = () => {
      this.updateIndicator(rowState);
    };
    rowState.line.addEventListener('scroll', rowState.onScroll, { passive: true });

    rowState.onPointerDown = (event) => {
      this.handleDragStart(rowState, event);
    };
    rowState.line.addEventListener('pointerdown', rowState.onPointerDown);

    rowState.onPointerMove = (event) => {
      this.handleDragMove(rowState, event);
    };
    rowState.line.addEventListener('pointermove', rowState.onPointerMove);

    rowState.onPointerUp = (event) => {
      this.handleDragEnd(rowState, event);
    };
    rowState.line.addEventListener('pointerup', rowState.onPointerUp);
    rowState.line.addEventListener('pointercancel', rowState.onPointerUp);
    rowState.line.addEventListener('lostpointercapture', rowState.onPointerUp);

    rowState.onClick = (event) => {
      if (!rowState.suppressClick) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      rowState.suppressClick = false;
    };
    rowState.line.addEventListener('click', rowState.onClick, true);

    rowState.onDragStart = (event) => {
      event.preventDefault();
    };
    rowState.line.addEventListener('dragstart', rowState.onDragStart);

    rowState.line.querySelectorAll('a, img').forEach((element) => {
      element.draggable = false;
    });

    if (rowState.prevButton) {
      rowState.onPrevClick = () => {
        this.scrollRow(rowState, -1);
      };
      rowState.prevButton.addEventListener('click', rowState.onPrevClick);
    }

    if (rowState.nextButton) {
      rowState.onNextClick = () => {
        this.scrollRow(rowState, 1);
      };
      rowState.nextButton.addEventListener('click', rowState.onNextClick);
    }
  }

  handleDragStart(rowState, event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) {
      return;
    }

    rowState.isPointerDown = true;
    rowState.isDragging = false;
    rowState.suppressClick = false;
    rowState.dragStartX = event.clientX;
    rowState.dragStartScrollLeft = rowState.line.scrollLeft;
    rowState.dragPointerId = event.pointerId;
  }

  handleDragMove(rowState, event) {
    if (!rowState.isPointerDown || event.pointerType !== 'mouse') {
      return;
    }

    const dragDistance = event.clientX - rowState.dragStartX;

    if (!rowState.isDragging && Math.abs(dragDistance) < 5) {
      return;
    }

    rowState.isDragging = true;
    rowState.suppressClick = true;
    rowState.line.classList.add('is-dragging');

    if (!rowState.line.hasPointerCapture(event.pointerId)) {
      rowState.line.setPointerCapture(event.pointerId);
    }

    rowState.line.scrollLeft = rowState.dragStartScrollLeft - dragDistance;
    event.preventDefault();
  }

  handleDragEnd(rowState, event) {
    if (!rowState.isPointerDown) {
      return;
    }

    rowState.isPointerDown = false;
    rowState.isDragging = false;
    rowState.line.classList.remove('is-dragging');

    if (
      rowState.dragPointerId !== null &&
      rowState.line.hasPointerCapture(rowState.dragPointerId)
    ) {
      rowState.line.releasePointerCapture(rowState.dragPointerId);
    }

    rowState.dragPointerId = null;
  }

  showMore() {
    if (this.activeRows >= this.rows.length) {
      return;
    }

    const prevVisibleRows = this.getVisibleRowsCount();
    this.activeRows = Math.min(this.rows.length, this.activeRows + this.rowStep);
    this.render(true, prevVisibleRows);
  }

  getVisibleRowsCount() {
    const hasMore = this.activeRows < this.rows.length;
    // Include the next row as a dimmed preview while the more button is visible.
    return Math.min(this.rows.length, this.activeRows + (hasMore ? 1 : 0));
  }

  render(animateNewRows = false, prevVisibleRows = 0) {
    const visibleRows = this.getVisibleRowsCount();
    const hasMore = this.activeRows < this.rows.length;
    const previewRowIndex = hasMore ? this.activeRows : -1;

    this.rows.forEach((rowState, index) => {
      const rowVisible = index < visibleRows;
      const wasVisible = !rowState.row.hidden;
      const isPreviewRow = index === previewRowIndex;
      const showNav = rowVisible && index < this.activeRows;
      const hasEnoughItemsForNav = rowState.items.length >= 3;

      rowState.row.hidden = !rowVisible;
      rowState.items.forEach((item) => item.classList.remove('is-dim'));

      if (!rowVisible) {
        rowState.row.classList.remove('is-enter');
        rowState.row.classList.remove('is-enter-active');
        return;
      }

      if (!wasVisible) {
        this.resetRowScroll(rowState);
      }

      if (isPreviewRow) {
        rowState.items.forEach((item) => item.classList.add('is-dim'));
      }

      if (rowState.nav) {
        rowState.nav.style.display = showNav && hasEnoughItemsForNav ? '' : 'none';
      }

      const isNewRow = animateNewRows && !wasVisible && index >= prevVisibleRows;
      if (isNewRow) {
        rowState.row.classList.add('is-enter');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            rowState.row.classList.add('is-enter-active');
          });
        });
      } else {
        rowState.row.classList.remove('is-enter');
        rowState.row.classList.add('is-enter-active');
      }
    });

    this.root.classList.toggle('is-has-more', hasMore);
    this.button.style.display = hasMore ? '' : 'none';

    this.rows.forEach((rowState) => {
      if (!rowState.row.hidden) {
        this.updateIndicator(rowState);
      }
    });
  }

  scrollRow(rowState, direction) {
    if (rowState.items.length === 0) {
      return;
    }

    const step = this.getRowScrollStep(rowState);
    const maxScroll = Math.max(0, rowState.line.scrollWidth - rowState.line.clientWidth);
    const currentIndex = step > 0 ? Math.round(rowState.line.scrollLeft / step) : 0;
    const targetIndex = Math.max(
      0,
      Math.min(rowState.items.length - 1, currentIndex + direction),
    );
    const targetLeft = Math.min(maxScroll, targetIndex * step);

    rowState.line.scrollTo({
      left: targetLeft,
      behavior: 'smooth',
    });
  }

  getRowScrollStep(rowState) {
    if (rowState.items.length === 0) {
      return 0;
    }

    const firstItem = rowState.items[0];
    const listStyle = window.getComputedStyle(rowState.line);
    const columnGap = parseFloat(listStyle.columnGap) || 0;

    return firstItem.getBoundingClientRect().width + columnGap;
  }

  updateIndicator(rowState) {
    if (!rowState.indicatorThumb) {
      return;
    }

    const maxScroll = rowState.line.scrollWidth - rowState.line.clientWidth;
    const progress = maxScroll > 0 ? rowState.line.scrollLeft / maxScroll : 0;
    const thumbWidth = maxScroll > 0 ? Math.max(progress * 100, 5) : 100;

    rowState.indicatorThumb.style.width = `${thumbWidth}%`;
    rowState.indicatorThumb.style.left = '0';

    if (rowState.prevButton) {
      rowState.prevButton.disabled = rowState.line.scrollLeft <= 1;
    }

    if (rowState.nextButton) {
      rowState.nextButton.disabled = rowState.line.scrollLeft >= maxScroll - 1;
    }
  }

  resetRowScroll(rowState) {
    rowState.line.scrollTo({
      left: 0,
      behavior: 'auto',
    });
  }

  syncRowsToStart() {
    this.rows.forEach((rowState) => {
      this.resetRowScroll(rowState);
      this.updateIndicator(rowState);
    });
  }
}
